"use client";

import { useState, useEffect } from "react";
import posthog from "posthog-js";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface UsePWAInstallReturn {
  deferredPrompt: BeforeInstallPromptEvent | null;
  isInstalled: boolean;
  canInstall: boolean;
  install: () => Promise<void>;
}

const PWA_INSTALLED_ANALYTICS_KEY = "pwaInstalledAnalyticsSent";

function markLocalInstalled(): void {
  localStorage.setItem("pwaInstalled", "true");
  if (!localStorage.getItem("pwaInstalledDate")) {
    localStorage.setItem("pwaInstalledDate", new Date().toISOString());
  }
}

/** Once per browser profile — avoids repeat `pwa_installed` on every standalone visit. */
function capturePwaInstalledOnce(
  detection: "appinstalled" | "standalone",
): void {
  if (localStorage.getItem(PWA_INSTALLED_ANALYTICS_KEY) === "1") {
    return;
  }
  localStorage.setItem(PWA_INSTALLED_ANALYTICS_KEY, "1");
  markLocalInstalled();
  posthog.capture("pwa_installed", { detection });
}

/**
 * Custom hook for managing PWA installation functionality
 * Handles beforeinstallprompt event, installation state, and PostHog install tracking
 */
export function usePWAInstall(): UsePWAInstallReturn {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Check if the app is already installed
    const checkInstalled = () => {
      const isStandalone =
        typeof window !== "undefined" &&
        window.matchMedia("(display-mode: standalone)").matches;
      return isStandalone;
    };

    if (checkInstalled()) {
      capturePwaInstalledOnce("standalone");
      const timer = setTimeout(() => {
        setIsInstalled(true);
        setCanInstall(false);
      }, 0);
      return () => clearTimeout(timer);
    }

    // Track user engagement (page views)
    const engagementKey = "pwaEngagementCount";
    const currentEngagement = parseInt(
      localStorage.getItem(engagementKey) || "0",
      10,
    );
    const newEngagement = currentEngagement + 1;
    localStorage.setItem(engagementKey, newEngagement.toString());

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      // Type guard to ensure it's a BeforeInstallPromptEvent
      if (
        "prompt" in e &&
        "userChoice" in e &&
        typeof (e as BeforeInstallPromptEvent).prompt === "function"
      ) {
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setCanInstall(true);
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
      capturePwaInstalledOnce("appinstalled");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const install = async (): Promise<void> => {
    if (!deferredPrompt) {
      throw new Error("Install prompt is not available");
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      posthog.capture(
        outcome === "accepted"
          ? "pwa_install_prompt_accepted"
          : "pwa_install_prompt_dismissed",
      );

      setDeferredPrompt(null);
      setCanInstall(false);
    } catch (error) {
      setDeferredPrompt(null);
      setCanInstall(false);
      throw error;
    }
  };

  return {
    deferredPrompt,
    isInstalled,
    canInstall,
    install,
  };
}
